import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';

// Import report components
import DailySalesReport from './DailySalesReport';
import MonthlySalesReport from './MonthlySalesReport';
import InventoryReport from './InventoryReport';
import CustomerReport from './CustomerReport';
import IndividualCustomerReport from './IndividualCustomerReport';
import PaymentMethodsReport from './PaymentMethodsReport';
import ExportButtons from './ExportButtons';

// Import utilities
import ReportFilters from './ReportFilters';
import { reportTypes } from './reportTypes';
import { reportingService } from '../../service/reportingService';

function Reporting() {
  const [selectedReport, setSelectedReport] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    locationId: null, // Changed to null to work with SelectLocation component
    customerId: null, // For individual customer reports
    type: '',
    period: 'monthly',
    page: 1, // For pagination
    limit: 50 // Records per page
  });

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    // Validation for daily-sales report
    if (selectedReport === 'daily-sales' && !filters.locationId) {
      setError('Please select a location for the daily sales report.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      switch (selectedReport) {
        case 'daily-sales':
          response = await reportingService.getDailySalesReport(
            filters.date, 
            filters.locationId, 
            filters.page, 
            filters.limit
          );
          break;
        case 'monthly-sales':
          response = await reportingService.getMonthlySalesReport(filters.year, filters.month, filters.locationId, filters.page, filters.limit);
          break;
        case 'inventory':
          response = await reportingService.getInventoryReport(filters.type);
          break;
        case 'customers':
          const dateParam = filters.period === 'daily' 
            ? filters.date 
            : `${filters.year}-${filters.month.toString().padStart(2, '0')}`;
          response = await reportingService.getCustomerReport(filters.period, dateParam);
          break;
        case 'individual-customer':
          if (!filters.customerId) {
            throw new Error('Please select a customer for individual customer report');
          }
          response = await reportingService.getIndividualCustomerReport(
            filters.customerId, 
            filters.startDate, 
            filters.endDate
          );
          break;
        case 'payment-methods':
          response = await reportingService.getPaymentMethodsReport(filters.startDate, filters.endDate);
          break;
        default:
          throw new Error('Invalid report type selected');
      }

      // The httpService already handles response.code checking through interceptors
      // If we get here, the response was successful (code === 0)
      setReportData(response.data || response);
    } catch (err) {
      console.error('API Error:', err);
      
      // The httpService already shows toast notifications for errors
      // We just need to set a local error state for component-specific handling
      if (err.response && err.response.status === 200 && err.response.data) {
        // This is a logical error (code: 1) that was caught by httpService
        setError(err.response.data.message || 'Report generation failed');
      } else {
        // This is a network or other error
        setError('Unable to generate report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    if (selectedReport === 'daily-sales') {
      if (!filters.locationId) {
        setError('Please select a location to navigate through pages.');
        return;
      }

      setFilters(prev => ({ ...prev, page }));
      
      setLoading(true);
      try {
        const response = await reportingService.getDailySalesReport(
          filters.date, 
          filters.locationId, 
          page, 
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'monthly-sales') {
      setFilters(prev => ({ ...prev, page }));
      
      setLoading(true);
      try {
        const response = await reportingService.getMonthlySalesReport(
          filters.year, 
          filters.month, 
          filters.locationId, 
          page, 
          filters.limit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Pagination Error:', err);
        setError('Unable to load page. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLimitChange = async (newLimit) => {
    if (selectedReport === 'daily-sales') {
      if (!filters.locationId) {
        setError('Please select a location to change page size.');
        return;
      }

      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when changing limit
      
      setLoading(true);
      try {
        const response = await reportingService.getDailySalesReport(
          filters.date, 
          filters.locationId, 
          1, // Always go to page 1 when changing limit
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    } else if (selectedReport === 'monthly-sales') {
      setFilters(prev => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when changing limit
      
      setLoading(true);
      try {
        const response = await reportingService.getMonthlySalesReport(
          filters.year, 
          filters.month, 
          filters.locationId, 
          1, // Always go to page 1 when changing limit
          newLimit
        );
        setReportData(response.data || response);
      } catch (err) {
        console.error('Limit Change Error:', err);
        setError('Unable to change page size. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const renderReportContent = () => {
    if (!reportData) return null;

    switch (selectedReport) {
      case 'daily-sales':
        return <DailySalesReport 
          data={reportData} 
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
        />;
      case 'monthly-sales':
        return <MonthlySalesReport 
          data={reportData} 
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentLimit={filters.limit}
        />;
      case 'inventory':
        return <InventoryReport data={reportData} />;
      case 'customers':
        return <CustomerReport data={reportData} />;
      case 'individual-customer':
        return <IndividualCustomerReport data={reportData} />;
      case 'payment-methods':
        return <PaymentMethodsReport data={reportData} />;
      default:
        return <Typography>Report content coming soon...</Typography>;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reporting Dashboard
      </Typography>
      
      {/* Report Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>Select Report Type</Typography>
        <Grid container spacing={2}>
          {reportTypes.map((report) => (
            <Grid item xs={12} sm={6} md={4} key={report.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: selectedReport === report.id ? 2 : 1,
                  borderColor: selectedReport === report.id ? report.color : 'divider',
                  '&:hover': { elevation: 4 }
                }}
                onClick={() => {
                  setSelectedReport(report.id);
                  setReportData(null); // Clear previous report data
                  setError(null); // Clear any previous errors
                  // Reset location filter when changing report types
                  setFilters(prev => ({ ...prev, locationId: null }));
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ color: report.color, mr: 1 }}>
                      <report.icon />
                    </Box>
                    <Typography variant="h6" component="div">
                      {report.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {report.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Filters */}
      <ReportFilters 
        selectedReport={selectedReport}
        filters={filters}
        setFilters={setFilters}
      />

      {/* Generate Button */}
      {selectedReport && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={
              loading || 
              (selectedReport === 'daily-sales' && !filters.locationId)
            }
            startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
          
          {reportData && (
            <ExportButtons
              reportType={selectedReport}
              reportData={reportData}
              filters={filters}
              onExportStart={(format) => {
                console.log(`Starting ${format} export...`);
              }}
              onExportComplete={(format, filename) => {
                console.log(`${format} export completed: ${filename}`);
              }}
              onExportError={(format, error) => {
                setError(`Export failed: ${error}`);
              }}
              disabled={loading}
            />
          )}
          
          {selectedReport === 'daily-sales' && !filters.locationId && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              Please select a location to generate the daily sales report
            </Typography>
          )}
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Report Content */}
      {reportData && (
        <Paper sx={{ p: 3 }}>
          {renderReportContent()}
        </Paper>
      )}
    </Box>
  );
}

export default Reporting;
